// https://linear.app/gometroapp/project/execution-board-task-notifications-791057aec941/overview

import type { ReactNode } from 'react'

import { DistanceInput, TimeInput } from '@/feature/alerts/components/InlineInputs'

import { AlertTemplateKey } from './alertContract'

export type AlertTemplateFieldValues = Record<string, number>

export interface AlertTemplateConfig {
  key: AlertTemplateKey
  title: string
  description: string
  /** Vehicle tags only apply to alerts that follow a specific vehicle. */
  taggableAlert?: boolean
  tooltipText?: ReactNode
  defaultFieldValues?: AlertTemplateFieldValues
  renderBody?: (
    values: AlertTemplateFieldValues,
    onChange: (key: string, value: number) => void,
    fieldErrorKeys?: Set<string>,
  ) => ReactNode
}

// Task Assignment Alerts

export const TaskAssignmentCreated: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_ASSIGNMENT_CREATED,
  title: 'You have been assigned a new task',
  description: '',
}

export const TaskAssignmentRejected: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_ASSIGNMENT_REJECTED,
  title: 'Task was rejected',
  description: 'Assign it to another company or your own vehicle',
}

export const TaskStateScheduled: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_STATE_SCHEDULED,
  title: 'Task scheduled',
  description: 'A vehicle has been assigned to your task',
}

export const TaskAssignmentStillness: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_TRIP_STILLNESS,
  title: 'Stillness Alert – Vehicle Not Moving',
  description: '',
  taggableAlert: true,
  tooltipText:
    'Alert fires once when a vehicle has been still longer than your set time. Multiple stillness alerts may trigger simultaneously if tracking data arrives infrequently — the system sends all exceeded thresholds at once. For faster detection, use a provider with frequent data updates.',
  defaultFieldValues: { time_buffer: 10 },
  renderBody: (values, onChange, errorKeys) => (
    <span>
      Alert me if the vehicle has not moved in{' '}
      <TimeInput
        value={values.time_buffer}
        onChange={(v) => onChange('time_buffer', v)}
        error={errorKeys?.has('time_buffer')}
      />
    </span>
  ),
}

// Start Location Alerts

export const TaskStartVehicleNotAssigned: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_START_VEHICLE_NOT_ASSIGNED,
  title: 'Task Start Time Approaching – No Vehicle Assigned',
  description: '',
  defaultFieldValues: { time_buffer: 30 },
  renderBody: (values, onChange, errorKeys) => (
    <span>
      Alert me{' '}
      <TimeInput
        value={values.time_buffer}
        onChange={(v) => onChange('time_buffer', v)}
        error={errorKeys?.has('time_buffer')}
      />{' '}
      before start time if no vehicle is assigned
    </span>
  ),
}

export const TaskStartVehiclePossiblyLate: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_START_VEHICLE_POSSIBLY_LATE,
  title: 'Task Start – Potential Delay',
  description: 'Vehicle unlikely to reach Start Location on time',
  taggableAlert: true,
  defaultFieldValues: { time_buffer: 30, distance: 5 },
  renderBody: (values, onChange, errorKeys) => (
    <span>
      Alert me{' '}
      <TimeInput
        value={values.time_buffer}
        onChange={(v) => onChange('time_buffer', v)}
        error={errorKeys?.has('time_buffer')}
      />{' '}
      before start time if the assigned vehicle is more than{' '}
      <DistanceInput
        value={values.distance}
        onChange={(v) => onChange('distance', v)}
        error={errorKeys?.has('distance')}
      />{' '}
      from the start location.
    </span>
  ),
}

export const TaskStartVehicleLate: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_START_VEHICLE_LATE,
  title: 'Task Start – Vehicle Late',
  description: 'Vehicle not at Start Location at start time',
  taggableAlert: true,
}

export const TaskStartVehicleStillLate: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_START_VEHICLE_STILL_LATE,
  title: 'Task Start – Vehicle Still Late',
  description: 'Vehicle still not at Start Location after start time',
  taggableAlert: true,
  defaultFieldValues: { time_buffer: 30 },
  renderBody: (values, onChange, errorKeys) => (
    <span>
      Alert me{' '}
      <TimeInput
        value={values.time_buffer}
        onChange={(v) => onChange('time_buffer', v)}
        error={errorKeys?.has('time_buffer')}
      />{' '}
      after start time while vehicle is still not at the start location
    </span>
  ),
}

export const TaskStartVehicleArrived: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_START_VEHICLE_ARRIVED,
  title: 'Task Start – Vehicle Arrived',
  description: 'Vehicle arrived at Start Location',
  taggableAlert: true,
}

// End Location Alerts

export const TaskEndVehiclePossiblyLate: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_END_VEHICLE_POSSIBLY_LATE,
  title: 'Task End – Potential Delay',
  description: 'Vehicle unlikely to reach End Location on time',
  taggableAlert: true,
  defaultFieldValues: { time_buffer: 30, distance: 5 },
  renderBody: (values, onChange, errorKeys) => (
    <span>
      Alert me{' '}
      <TimeInput
        value={values.time_buffer}
        onChange={(v) => onChange('time_buffer', v)}
        error={errorKeys?.has('time_buffer')}
      />{' '}
      before end time if the assigned vehicle is more than{' '}
      <DistanceInput
        value={values.distance}
        onChange={(v) => onChange('distance', v)}
        error={errorKeys?.has('distance')}
      />{' '}
      from the end location.
    </span>
  ),
}

export const TaskEndVehicleLate: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_END_VEHICLE_LATE,
  title: 'Task End – Vehicle Late',
  description: 'Vehicle not at End Location at end time',
  taggableAlert: true,
}

export const TaskEndVehicleStillLate: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_END_VEHICLE_STILL_LATE,
  title: 'Task End – Vehicle Still Late',
  description: 'Vehicle not at End Location after end time',
  taggableAlert: true,
  defaultFieldValues: { time_buffer: 30 },
  renderBody: (values, onChange, errorKeys) => (
    <span>
      Alert me{' '}
      <TimeInput
        value={values.time_buffer}
        onChange={(v) => onChange('time_buffer', v)}
        error={errorKeys?.has('time_buffer')}
      />{' '}
      after end time while vehicle is still not at the end location
    </span>
  ),
}

export const TaskEndVehicleArrived: AlertTemplateConfig = {
  key: AlertTemplateKey.TASK_END_VEHICLE_ARRIVED,
  title: 'Task End – Vehicle Arrived',
  description: 'Vehicle arrived at End Location',
  taggableAlert: true,
}

export const ALERT_TEMPLATE_BY_KEY: Record<AlertTemplateKey, AlertTemplateConfig> = {
  [AlertTemplateKey.TASK_ASSIGNMENT_CREATED]: TaskAssignmentCreated,
  [AlertTemplateKey.TASK_ASSIGNMENT_REJECTED]: TaskAssignmentRejected,
  [AlertTemplateKey.TASK_STATE_SCHEDULED]: TaskStateScheduled,
  [AlertTemplateKey.TASK_TRIP_STILLNESS]: TaskAssignmentStillness,
  [AlertTemplateKey.TASK_START_VEHICLE_NOT_ASSIGNED]: TaskStartVehicleNotAssigned,
  [AlertTemplateKey.TASK_START_VEHICLE_POSSIBLY_LATE]: TaskStartVehiclePossiblyLate,
  [AlertTemplateKey.TASK_START_VEHICLE_LATE]: TaskStartVehicleLate,
  [AlertTemplateKey.TASK_START_VEHICLE_STILL_LATE]: TaskStartVehicleStillLate,
  [AlertTemplateKey.TASK_START_VEHICLE_ARRIVED]: TaskStartVehicleArrived,
  [AlertTemplateKey.TASK_END_VEHICLE_POSSIBLY_LATE]: TaskEndVehiclePossiblyLate,
  [AlertTemplateKey.TASK_END_VEHICLE_LATE]: TaskEndVehicleLate,
  [AlertTemplateKey.TASK_END_VEHICLE_STILL_LATE]: TaskEndVehicleStillLate,
  [AlertTemplateKey.TASK_END_VEHICLE_ARRIVED]: TaskEndVehicleArrived,
}
